/**
 * Created by felipe on 23/1/17.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import idx from 'idx';
import { QueryRenderer } from 'react-relay';
import { isEmpty } from 'lodash';
import {
  batchMiddleware,
  errorMiddleware,
  loggerMiddleware,
  perfMiddleware,
  RelayNetworkLayer,
  urlMiddleware,
} from 'react-relay-network-modern/es';
import { Environment, Network, QueryResponseCache, RecordSource, Store } from 'relay-runtime';
import RelayFeatureFlags from 'relay-runtime/lib/util/RelayFeatureFlags';
import { store } from '../store';
import { ApiServer, BatchApiServer } from '../constants';
import Loading from '../components/Loading';
import RelayError from '../components/RelayError';
import fetchGraphQLWithRetry from './fetchGraphQLWithRetry';

RelayFeatureFlags.ENABLE_RELAY_CONTAINERS_SUSPENSE = false;

const oneMinute = 60 * 1000;
export const cache = new QueryResponseCache({ size: 250, ttl: 10 * oneMinute });
const source = new RecordSource();
const modernStore = new Store(source, { gcReleaseBufferSize: 1000 });

// Define a function that fetches the results of an operation (query/mutation/etc)
// and returns its results as a Promise:
function fetchQuery(operation, variables, cacheConfig, uploadables) {
  const queryID = operation.text;

  const isMutation = operation.operationKind === 'mutation';
  const isQuery = operation.operationKind === 'query';
  const forceFetch = !!idx(cacheConfig, (_) => _.force);

  // Try to get data from cache on queries
  const fromCache = cache.get(queryID, variables);
  if (isQuery && fromCache !== null && !forceFetch) {
    return fromCache;
  }

  const headers = {
    Accept: 'application/json',
    Authorization: `JWT ${store.getState().user.token}`,
  };

  const requestVariables = {
    method: 'POST',
    credentials: 'include',
    mode: 'cors',
    headers,
  };

  if (!isEmpty(uploadables)) {
    if (!window.FormData) {
      throw new Error('Uploading files without `FormData` not supported.');
    }

    const formData = new FormData();
    formData.append('query', operation.text);
    formData.append('variables', JSON.stringify(variables));

    Object.keys(uploadables).forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(uploadables, key)) {
        formData.append(key, uploadables[key]);
      }
    });

    requestVariables.body = formData;
  } else {
    requestVariables.headers['Content-Type'] = 'application/json';

    requestVariables.body = JSON.stringify({
      query: operation.text,
      variables,
    });
  }

  return fetchGraphQLWithRetry({ operation, url: ApiServer, requestInit: requestVariables })
    .then((json) => {
      // Update cache on queries
      if (isQuery && json) {
        cache.set(queryID, variables, json);
      }
      // Clear cache on mutations
      if (isMutation) {
        cache.clear();
      }

      return json;
      // })
      //   .then(jsonPayload => {
      //   if (jsonPayload.data && queryID) {
      //     cache.set(queryID, variables, jsonPayload.data);
      //     return jsonPayload;
      //   }
      //
      //   return jsonPayload;
      // errors etc
    })
    .catch((err) => {
      console.log(`Relay Error: ${err}`, err);
      throw err;
    });
}

// CreateNote a networkAuthenticated layer from the fetch function
const networkAuthenticated = Network.create(fetchQuery);

export const ModernEnvAuthenticated = new Environment({
  network: networkAuthenticated,
  store: modernStore,
});

// Define a function that fetches the results of an operation (query/mutation/etc)
// and returns its results as a Promise:
function fetchPublicQuery(operation, variables, cacheConfig) {
  const queryID = operation.text;

  const isMutation = operation.operationKind === 'mutation';
  const isQuery = operation.operationKind === 'query';
  const forceFetch = !!idx(cacheConfig, (_) => _.force);

  // Try to get data from cache on queries
  const fromCache = cache.get(queryID, variables);
  if (isQuery && fromCache !== null && !forceFetch) {
    return fromCache;
  }

  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };

  // console.log('fetchQuery: ', { operation, variables, cacheConfig });
  const requestVariables = {
    method: 'POST',
    headers,
    body: JSON.stringify({
      query: operation.text, // GraphQL text from input
      variables,
    }),
  };

  return fetchGraphQLWithRetry({ operation, url: ApiServer, requestInit: requestVariables })
    .then((json) => {
      // Update cache on queries
      if (isQuery && json) {
        cache.set(queryID, variables, json);
      }
      // Clear cache on mutations
      if (isMutation) {
        cache.clear();
      }

      return json;
      // })
      //   .then(jsonPayload => {
      //   if (jsonPayload.data && queryID) {
      //     cache.set(queryID, variables, jsonPayload.data);
      //     return jsonPayload;
      //   }
      //
      //   return jsonPayload;
      // errors etc
    })
    .catch((err) => {
      console.log(`Relay Error: ${err}`, err);
      throw err;
    });
}

// CreateNote a networkAuthenticated layer from the fetch function
const networkPublic = Network.create(fetchPublicQuery);

const publicSource = new RecordSource();
const publicStore = new Store(publicSource);
export const ModernEnvPublic = new Environment({
  network: networkPublic,
  store: publicStore,
});

const customNetwork = new RelayNetworkLayer([
  // example of the custom inline middleware
  (next) => async (req) => {
    const forceFetch = !!idx(req, (_) => _.cacheConfig.force);

    if (req.isMutation()) {
      cache.clear();
      return next(req);
    }

    if (forceFetch) {
      const queryId = req.getID();
      const variables = req.getVariables();
      const res = await next(req);

      if (!res.errors) {
        cache.set(queryId, variables, res);
      }
      return res;
    }

    try {
      const queryId = req.getID();
      const variables = req.getVariables();

      const cachedRes = cache.get(queryId, variables);
      if (cachedRes) {
        return cachedRes;
      }

      const res = await next(req);
      if (!res.errors) {
        cache.set(queryId, variables, res);
      }

      return res;
    } catch (e) {
      // if error, just log it to console
      console.log(e); // eslint-disable-line
      console.log('RRN Error: ', { queryId: req.getID() }); // eslint-disable-line
    }

    return next(req);
  },
  urlMiddleware({
    url: () => ApiServer,
  }),
  batchMiddleware({
    batchUrl: () => BatchApiServer,
    batchTimeout: 10,
  }),
  process.env.API_URL !== 'production' && false ? loggerMiddleware() : null,
  process.env.API_URL !== 'production' && false ? errorMiddleware() : null,
  process.env.API_URL !== 'production' && false ? perfMiddleware() : null,
  (next) => async (req) => {
    req.fetchOpts.headers.Accept = 'application/json';
    req.fetchOpts.headers['Content-Type'] = 'application/json';

    const decodedCookie = decodeURIComponent(document.cookie); // eslint-disable-line
    const ca = decodedCookie.split(';');
    let sessionCookie;
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') {
        c = c.substring(1);
      }
      if (c.indexOf('sessionid=') === 0) {
        sessionCookie = c.substring('sessionid='.length, c.length);
        // console.log(`Cookie:  ${c}`);
      }
    }
    if (sessionCookie && false) {
      req.fetchOpts.headers['X-Cookie'] = sessionCookie;
    } else {
      // console.log(`No Cookie:  ${document.cookie}`);
      req.fetchOpts.headers.Authorization = `JWT ${store.getState().user.token}`;
    }
    const res = await next(req);
    return res;
  },
]);

// export const ModernEnvAuthenticated = new Environment({
//   customNetwork,
//   store: modernStore,
// });

class RelayErrorBoundary extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, info: error };
  }

  componentDidCatch(error, info) {
    const { componentName, enableLogging } = this.props;

    // Display fallback UI
    // You can also log the error to an error reporting service
    // logErrorToMyService(error, info);
    if (enableLogging) {
      console.log(`RelayErrorBoundary ${componentName}:`, info);
    }
  }

  render() {
    const { children, renderFailure } = this.props;
    const { hasError, info } = this.state;

    if (hasError) {
      if (renderFailure) {
        return renderFailure(info);
      }
      return <RelayError error={info} retry={() => this.setState({ hasError: false })} />;
    }

    return children;
  }
}

RelayErrorBoundary.propTypes = {
  children: PropTypes.element,
  componentName: PropTypes.string,
  enableLogging: PropTypes.bool,
  renderFailure: PropTypes.func,
};

export function createQueryRenderer2(Component, config) {
  const {
    queriesParams,
    renderLoading,
    renderFailure,
    query,
    initialVariables,
    returnProps,
    enableLogging,
    componentName,
    getComponentName,
    cacheTTL,
    forceFetch,
  } = config;

  const finalComponentName = getComponentName
    ? getComponentName(componentName || Component.name)
    : componentName || Component.name;

  const _computeQueryConfig = (props) => {
    if (enableLogging) {
      console.log(`createQueryRenderer2 ${finalComponentName} _computeQueryConfig...`, props);
    }
    const params = typeof queriesParams === 'function' ? queriesParams(props) : {};
    return { ...initialVariables, ...params };
  };

  const RelayQueryRendererWrapper = (props) => {
    const componentUnmounting = useRef(false);
    const [deletingNode, setDeletingNode] = useState(false);
    const [deletingNodeCallback, setDeletingNodeCallback] = useState(null);
    const [getQueryVariables, set__RENAME] = useState(_computeQueryConfig(props));
    const cacheConfig = {};

    if (cacheTTL) cacheConfig.ttl = cacheTTL;
    if (forceFetch) cacheConfig.force = true;

    useEffect(() => {
      if (enableLogging) {
        console.log(`createQueryRenderer2 ${finalComponentName} constructor props:`);
        console.log({ props, Component });
        console.log(`createQueryRenderer2 ${finalComponentName} DID MOUNT`);
      }

      return function () {
        componentUnmounting.current = true;
        if (enableLogging) {
          console.log(`createQueryRenderer2 ${finalComponentName} will unmount`);
        }
      };
    }, []);

    useEffect(() => {
      set__RENAME(_computeQueryConfig(props));
    }, [props]);

    useEffect(() => {
      if (deletingNode && typeof deletingNodeCallback === 'function') {
        deletingNodeCallback(() => {
          setDeletingNode(false);
          setDeletingNodeCallback(null);
        });
      }
    }, [deletingNode, deletingNodeCallback]);

    if (enableLogging) {
      console.log(`createQueryRenderer2 ${finalComponentName} render started`, {
        getQueryVariables,
        deletingNode,
        deletingNodeCallback,
      });
    }

    const toggleDeletingNode = useCallback(
      (cb) => {
        setDeletingNode(true);
        setDeletingNodeCallback(cb);
      },
      [setDeletingNode, setDeletingNodeCallback],
    );

    return (
      <RelayErrorBoundary
        componentName={finalComponentName}
        enableLogging={enableLogging}
        renderFailure={renderFailure}
      >
        <QueryRenderer
          environment={ModernEnvAuthenticated}
          query={query}
          cacheConfig={cacheConfig}
          variables={getQueryVariables}
          render={({ error, props: queryReturnProps }) => {
            if (enableLogging) {
              console.log(`createQueryRenderer2 ${finalComponentName} render callback`);
            }

            if (queryReturnProps && !deletingNode) {
              let getProps = {};
              if (returnProps) {
                getProps = returnProps(queryReturnProps);
              }
              if (enableLogging) {
                console.log(`createQueryRenderer2 ${finalComponentName} RENDER w/ data:`);
                console.log({ queryReturnProps, getProps });
              }
              return (
                <Component
                  toggleDeletingNode={toggleDeletingNode}
                  {...props}
                  {...getProps}
                  {...queryReturnProps}
                />
              );
            }
            if (enableLogging) {
              console.log(`createQueryRenderer2 ${finalComponentName} LOADING w/ data:`);
              console.log({ queryReturnProps, error });
            }
            if (renderLoading) {
              return renderLoading(props);
            }
            return <Loading />;
          }}
        />
      </RelayErrorBoundary>
    );
  };

  //
  //   componentWillReceiveProps(nextProps
  // :
  //   Object;
  // )
  //   {
  //     if (nextProps.location) {
  //       if (this.props.location.pathname !== nextProps.location.pathname) {
  //         if (enableLogging) {
  //           console.log(`createQueryRenderer2 ${finalComponentName} componentWillReceiveProps:`);
  //           console.log('Setting new STATE due to route change...', nextProps);
  //         }
  //         if (!this.componentUnmounting) {
  //           this.setState({
  //             getQueryVariables: this._computeQueryConfig(nextProps),
  //           });
  //         }
  //       }
  //     } else {
  //       if (enableLogging) {
  //         console.log(`createQueryRenderer2 ${finalComponentName} componentWillReceiveProps:`);
  //       }
  //       if (shouldComponentUpdate(this, nextProps)) {
  //         if (enableLogging) {
  //           console.log('New Props...', nextProps);
  //         }
  //         if (!this.componentUnmounting) {
  //           this.setState({
  //             getQueryVariables: this._computeQueryConfig(nextProps),
  //           });
  //         }
  //       } else {
  //         if (enableLogging) {
  //           console.log('Keeping state. Same Route...', nextProps);
  //         }
  //       }
  //     }
  //   }

  // const MergedComponent = hoistStatics(RelayQueryRendererWrapper, Component);
  //
  // console.dir({ MergedComponent, RelayQueryRendererWrapper, Component });
  // RelayQueryRendererWrapper.whyDidYouRender = true;
  return RelayQueryRendererWrapper;
}
